import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";

const FIELDS = [["marca", "Marca *"], ["modelo", "Modelo *"], ["serie", "Número de serie"], ["ip", "Dirección IP *"], ["puerto", "Puerto"], ["mac", "MAC"],
  ["firmware", "Firmware"], ["software", "Software / plataforma asociada"], ["usuario", "Usuario de administración"], ["contrasena", "Contraseña de administración"], ["ubicacion", "Ubicación física *"]];
const TECHS = [["huella", "Huella"], ["facial", "Facial"], ["tarjeta", "Tarjeta"], ["combinada", "Combinada"]];
const REQ = ["marca", "modelo", "ip", "ubicacion"];
const sel = "h-10 w-full rounded-md border border-input bg-background px-3 text-sm";
const AC = "control_acceso";

export const pdfHasCredsOption = (wo) => ["videovigilancia", AC].includes(wo.business_unit_code) || (wo.extra_services || []).some((s) => !s.removed && s.business_unit_code === AC);

const Terminal = ({ t, i, set, del, ed, tid }) => (
  <div className="space-y-2 rounded-lg border border-border p-3" data-testid={`${tid}-${i}`}>
    <div className="flex items-center justify-between">
      <span className="text-sm font-semibold">Terminal IP de asistencia {i + 1}</span>
      {ed && <Button size="sm" variant="ghost" onClick={del} data-testid={`${tid}-${i}-remove`}><Trash2 className="h-4 w-4" /></Button>}
    </div>
    <div className="grid gap-2 sm:grid-cols-2">
      {FIELDS.map(([k, l]) => (
        <div key={k} className="space-y-1"><Label className="text-xs text-muted-foreground">{l}</Label>
          <Input value={t[k] || ""} disabled={!ed} onChange={(e) => set({ ...t, [k]: e.target.value })} data-testid={`${tid}-${i}-${k}`} /></div>
      ))}
      <div className="space-y-1"><Label className="text-xs text-muted-foreground">Tecnología</Label>
        <select className={sel} value={t.tecnologia || ""} disabled={!ed} onChange={(e) => set({ ...t, tecnologia: e.target.value })} data-testid={`${tid}-${i}-tecnologia`}>
          <option value="">Sin especificar</option>{TECHS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select></div>
    </div>
    <Textarea rows={2} placeholder="Observaciones" value={t.observaciones || ""} disabled={!ed} onChange={(e) => set({ ...t, observaciones: e.target.value })} data-testid={`${tid}-${i}-observaciones`} />
  </div>
);

export const AcTerminals = ({ rows, editable, onSave, testid = "ac-terminals" }) => {
  const [list, setList] = useState(rows || []);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const upd = (next) => { setList(next); setDirty(true); };
  const missing = list.some((t) => REQ.some((k) => !(t[k] || "").trim()));
  const save = async () => {
    setBusy(true);
    try { setList((await onSave(list)) || []); setDirty(false); toast.success("Equipos intervenidos guardados"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid={testid}>
      <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-primary">Equipos intervenidos · terminales IP de asistencia</div>
      {list.length === 0 && <p className="text-sm text-muted-foreground" data-testid={`${testid}-empty`}>Sin terminales registradas en este servicio.</p>}
      {list.map((t, i) => <Terminal key={t.id || `new-${i}`} t={t} i={i} ed={editable} tid={testid} set={(v) => upd(list.map((x, j) => (j === i ? v : x)))} del={() => upd(list.filter((_, j) => j !== i))} />)}
      {editable && <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={() => upd([...list, { tecnologia: "" }])} data-testid={`${testid}-add`}><Plus className="mr-1 h-4 w-4" />Agregar terminal</Button>
        {dirty && <Button onClick={save} disabled={busy || missing} data-testid={`${testid}-save`}>{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Guardar equipos intervenidos</Button>}
      </div>}
      {editable && dirty && missing && <p className="text-xs text-amber-300" data-testid={`${testid}-missing`}>Completa marca, modelo, dirección IP y ubicación física de cada terminal.</p>}
    </div>
  );
};

export const MainAcTerminals = ({ wo, setWo, editable }) => wo.business_unit_code !== AC ? null : (
  <AcTerminals rows={wo.tech_data?.ac_terminals} editable={editable} testid="ac-terminals-main" onSave={async (rows) => {
    const { data } = await api.put(`/work-orders/${wo.id}/tech-data`, { ac_terminals: rows });
    setWo((w) => ({ ...w, tech_data: data }));
    return data.ac_terminals;
  }} />
);

export const SvcAcTerminals = ({ wo, s, editable, onSvc, n }) => s.business_unit_code !== AC ? null : (
  <AcTerminals rows={s.ac_terminals} editable={editable} testid={`ac-terminals-svc-${n}`} onSave={async (rows) => {
    const { data } = await api.put(`/work-orders/${wo.id}/services/${s.id}/tech`, { ac_terminals: rows });
    onSvc(data);
    return data.ac_terminals;
  }} />
);
